// Recorder di session replay (opt-in `replay`). Registra la sessione con rrweb,
// bufferizza gli eventi e li invia in chunk (flush periodico + su
// `pagehide`/`visibilitychange(hidden)`). Twin strutturale di `installPageviews`.
//
// rrweb NON è una dipendenza del pacchetto (che resta zero-dep): il recorder usa
// il globale `window.rrweb` fornito dalla pagina (build UMD di rrweb via
// script/importmap), coerentemente col modello di distribuzione dell'SDK. Il
// `record` è iniettabile per i test → nessun rrweb nel test path.
//
// Correlazione con gli errori server: al primo avvio la sessione scrive un cookie
// `cyi_replay` (id opaco). Le richieste same-origin lo trasportano → il gem
// `closeyourit-ruby` lo legge e lega gli errori server allo stesso replay. Gli
// errori JS invece portano l'id in `contexts.replay.replay_id` (via lo scope).

import type { Client } from '../../client'
import { buildReplayChunk } from '../../replay'
import { ensureReplaySession } from '../../session'
import type { StorageLike } from '../../session'

/// Opzioni passate al recorder rrweb (sottoinsieme usato).
export interface ReplayRecordOptions {
  emit: (event: unknown) => void
  maskAllInputs: boolean
  blockSelector: string | undefined
  maskTextSelector: string | undefined
}

/// Dipendenze iniettabili (test).
export interface ReplayDeps {
  /// Avvia la registrazione e ritorna la funzione di stop. Default: `window.rrweb`.
  record?: (opts: ReplayRecordOptions) => () => void
  /// Storage per la sessione (`undefined` = default, `null` = fallback in-memory).
  storage?: StorageLike | null
  /// Clock (test).
  now?: () => number
  /// Scrive l'id dove le richieste same-origin lo trasportano al backend (default:
  /// cookie `cyi_replay`). Iniettabile per i test.
  writeCorrelation?: (id: string) => void
}

// Eventi rrweb accumulati oltre cui forzare un flush: evita chunk illimitati sotto
// interazioni intense (rrweb emette molti eventi durante scroll/typing).
const REPLAY_MAX_BUFFER = 200

/// Avvia il recorder. Ritorna la funzione di disinstallazione (stop rrweb + flush
/// finale + rimozione listener/interval).
export function installReplay(client: Client, deps: ReplayDeps = {}): () => void {
  const session = ensureReplaySession(deps.storage, deps.now)
  client.setReplaySessionId(session.id)
  ;(deps.writeCorrelation ?? writeReplayCookie)(session.id)

  let buffer: unknown[] = []
  let seq = 0

  const flush = (): void => {
    if (buffer.length === 0) return
    const events = buffer
    buffer = []
    client.captureReplayChunk(
      buildReplayChunk({
        replaySessionId: session.id,
        seq: seq++,
        startedAt: session.startedAt,
        events,
        path: currentPath(),
        user: client.scope.userId,
      }),
    )
  }

  const record = deps.record ?? defaultRecord
  const stop = record({
    emit: (event) => {
      buffer.push(event)
      if (buffer.length >= REPLAY_MAX_BUFFER) flush()
    },
    maskAllInputs: client.options.replayMaskAllInputs,
    blockSelector: client.options.replayBlockSelector,
    maskTextSelector: client.options.replayMaskTextSelector,
  })

  const interval = setInterval(flush, client.options.replayFlushIntervalMs)
  const onHide = (): void => flush()
  const onVisibility = (): void => {
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
      flush()
    }
  }

  if (typeof window !== 'undefined') {
    window.addEventListener('pagehide', onHide)
  }
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility)
  }

  return () => {
    clearInterval(interval)
    if (typeof window !== 'undefined') {
      window.removeEventListener('pagehide', onHide)
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', onVisibility)
    }
    stop()
    flush()
  }
}

// Pathname corrente al flush (senza query string): alimenta le "pagine visitate"
// della galleria. `undefined` fuori dal browser.
function currentPath(): string | undefined {
  return typeof location !== 'undefined' ? location.pathname : undefined
}

/// Forma minima della build globale rrweb (`window.rrweb`).
interface RrwebGlobal {
  record: (opts: Record<string, unknown>) => (() => void) | undefined
}

// Recorder di default: usa `window.rrweb` se presente, altrimenti no-op (il
// replay resta inattivo, il resto dell'SDK invariato). Nessun import statico di
// rrweb → il pacchetto core resta zero-dep.
function defaultRecord(opts: ReplayRecordOptions): () => void {
  const rrweb = (globalThis as { rrweb?: RrwebGlobal }).rrweb
  if (!rrweb || typeof rrweb.record !== 'function') return () => {}
  const stop = rrweb.record({
    emit: (event: unknown) => opts.emit(event),
    maskAllInputs: opts.maskAllInputs,
    ...(opts.blockSelector ? { blockSelector: opts.blockSelector } : {}),
    ...(opts.maskTextSelector ? { maskTextSelector: opts.maskTextSelector } : {}),
  })
  return typeof stop === 'function' ? stop : () => {}
}

// Cookie di correlazione: session cookie, `SameSite=Lax`, id opaco (no PII). Le
// richieste same-origin (Turbo, XHR, form, navigazioni full-page) lo trasportano
// → il gem server lega gli errori server al replay. Scritto solo a replay attivo,
// che richiede il consenso dell'utente.
function writeReplayCookie(id: string): void {
  if (typeof document === 'undefined') return
  try {
    document.cookie = `cyi_replay=${encodeURIComponent(id)}; path=/; SameSite=Lax`
  } catch {
    // ambienti senza cookie: la correlazione server è best-effort
  }
}
